import { useQuery } from '@tanstack/react-query'
import { getPortfolio } from '../api/trading'
import { useAuthStore } from '../auth/useAuthStore'
import { portfolioPollMs } from '../lib/marketHours'
import type { Order, Position, PortfolioResponse } from '../api/types'

export const PENDING_STATUSES = /^(NEW|PENDING|PARTIALLY_FILLED|ACCEPTED|QUEUED|OPEN|WORKING)/i

function num(v: string | undefined): number {
  if (!v) return 0
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

function orderReserve(o: Order): number {
  if (o.side !== 'BUY') return 0
  if (o.notionalValue) return num(o.notionalValue)
  const remaining = num(o.quantity) - num(o.filledQuantity)
  const price = num(o.limitPrice) || num(o.averagePrice) || num(o.stopPrice)
  return remaining * price
}

export interface PortfolioSummary {
  data: PortfolioResponse | undefined
  isLoading: boolean
  error: unknown

  stockPositions: Position[]
  optionPositions: Position[]
  stocksValue: number
  optionsValue: number
  positionsValue: number
  dayChange: number

  cashValue: number
  cashPercentOfPortfolio: number | null
  portfolioValue: number

  bp: number
  cashBp: number
  optionsBp: number
  unsettled: number

  pendingOrders: Order[]
  pendingReserve: number
}

export function usePortfolioSummary(): PortfolioSummary {
  const accountId = useAuthStore((s) => s.accountId)
  const { data, isLoading, error } = useQuery({
    queryKey: ['portfolio', accountId],
    queryFn: getPortfolio,
    refetchInterval: portfolioPollMs,
    enabled: !!accountId,
  })

  if (!data) {
    return {
      data,
      isLoading,
      error,
      stockPositions: [],
      optionPositions: [],
      stocksValue: 0,
      optionsValue: 0,
      positionsValue: 0,
      dayChange: 0,
      cashValue: 0,
      cashPercentOfPortfolio: null,
      portfolioValue: 0,
      bp: 0,
      cashBp: 0,
      optionsBp: 0,
      unsettled: 0,
      pendingOrders: [],
      pendingReserve: 0,
    }
  }

  const stockPositions = data.positions.filter((p) => p.instrument.type === 'EQUITY')
  const optionPositions = data.positions.filter((p) => p.instrument.type === 'OPTION')
  const stocksValue = stockPositions.reduce((s, p) => s + num(p.currentValue), 0)
  const optionsValue = optionPositions.reduce((s, p) => s + num(p.currentValue), 0)
  const positionsValue = data.positions.reduce((s, p) => s + num(p.currentValue), 0)
  const dayChange = data.positions.reduce(
    (s, p) => s + num(p.positionDailyGain?.gainValue),
    0,
  )

  const cashEquity = data.equity.find((e) => e.type === 'CASH')
  const cashValue = num(cashEquity?.value)
  const cashPercentOfPortfolio = cashEquity?.percentageOfPortfolio
    ? Number(cashEquity.percentageOfPortfolio)
    : null
  const portfolioValue = positionsValue + cashValue

  const bp = num(data.buyingPower.buyingPower)
  const cashBp = num(data.buyingPower.cashOnlyBuyingPower)
  const optionsBp = num(data.buyingPower.optionsBuyingPower)
  const unsettled = Math.max(0, bp - cashBp)

  const pendingOrders = data.orders.filter((o) => PENDING_STATUSES.test(o.status))
  const pendingReserve = pendingOrders.reduce((s, o) => s + orderReserve(o), 0)

  return {
    data,
    isLoading,
    error,
    stockPositions,
    optionPositions,
    stocksValue,
    optionsValue,
    positionsValue,
    dayChange,
    cashValue,
    cashPercentOfPortfolio,
    portfolioValue,
    bp,
    cashBp,
    optionsBp,
    unsettled,
    pendingOrders,
    pendingReserve,
  }
}
